/**
 * The docket — `design_handoff_docket`, "The docket".
 *
 * A DECISION QUEUE, NOT A DOCUMENT LIBRARY. Three rows, each carrying the four things a reader
 * needs before he opens anything: what it is called, whose it is, what it does in one sentence,
 * and where it stands. The four thousand words behind them open one at a time.
 *
 * THE THRESHOLD IS SAID ONCE, at the top, because it is a fact about the NIGHT. See the note in
 * `domain/docket.ts` on what it replaced.
 *
 * WHAT THIS FILE MAY NOT DO. It may not edit, reflow, summarise in place, spell-correct or
 * truncate a proposal's text: that copy is authored by GMs and is rendered verbatim through
 * `RichView`, the same way the room renders it. The gist is separate copy, written by somebody,
 * and it is hidden the moment the row is expanded.
 *
 * `dq`, not `dk`. `DocketOrder.tsx` — the commissioner's reorder list — already owns `.dkrow` and
 * `.dkwho`, and this stylesheet has paid for a short shared class name four times over (see the
 * notes on `.who` and `.btn`). Two lists of the same items under two prefixes is cheap; one
 * prefix quietly restyling the other is not.
 */

import { useState } from 'react';
import { managerShort } from '../domain/identity.js';
import {
  decidedSummary, docketFoot, docketHead, docketRows, nightThreshold,
  type DocketInput, type DocketPhase, type DocketRow,
} from '../domain/docket.js';
import { GmBadge } from './GmBadge.js';
import type { RuleProposal } from '../store/types.js';
import { RichView } from './Rich.js';
import { Citations } from './RuleText.js';
import { Transcribed } from './Transcribed.js';

export interface DocketProps extends DocketInput {
  phase: DocketPhase;
  seats: number;
  /** Bodies keyed by proposal id — kept out of the row model, which is a decision, not a document. */
  bodyOf: (id: string) => string;
  /** The stored proposal behind a row, for the facts that are about the record rather than the vote. */
  recordOf: (id: string) => RuleProposal | undefined;
  closedLabel?: string;
  committedLabel?: string;
  /** Offered on the one line a stale draft gets. */
  onDiscardDraft?: (id: string) => void;
}

export function Docket(props: DocketProps) {
  const {
    phase, seats, bodyOf, recordOf, closedLabel, committedLabel, onDiscardDraft, ...input
  } = props;
  /* ACCORDION: one at a time, so the page never becomes a four-thousand-word scroll. */
  const [open, setOpen] = useState<string | null>(null);

  const rows = docketRows(input);
  const head = docketHead(phase, rows.length);
  const represented = input.represented ?? seats;
  const threshold = nightThreshold({
    represented, seats, revotes: rows.filter((r) => r.revote).length,
  });
  const decided = phase === 'committed';

  return (
    <section className="dqcard" aria-label={head.title}>
      <header className="dqhead">
        <div>
          <h2>{head.title}</h2>
          <p>{decided ? 'in the order they were heard' : head.sub}</p>
        </div>
        <span className="dqcount">{head.count}</span>
      </header>

      {/*
        SUPPRESSED ONCE IT IS OVER. Outcomes have superseded it: what an item needed is a fact
        about a vote that has already happened, and it is on the row that happened.
      */}
      {!decided && rows.length > 0 && (
        <div className={`dqthresh${threshold.uniform ? '' : ' split'}`}>
          <b>{threshold.said}</b>
          <span>{threshold.why}</span>
        </div>
      )}

      <ol className="dqrows">
        {rows.map((r) => (
          <Row key={r.id} row={r} open={open === r.id} body={bodyOf(r.id)}
            record={recordOf(r.id)}
            onToggle={() => setOpen((o) => (o === r.id ? null : r.id))}
            onDiscardDraft={onDiscardDraft} />
        ))}
        {rows.length === 0 && <li className="dqempty">Nothing on the docket yet.</li>}
      </ol>

      <p className="dqfoot">
        {docketFoot({
          phase,
          closedAt: closedLabel ?? null,
          committedAt: committedLabel ?? null,
          closedLabel,
          committedLabel,
        })}
        {decided && rows.length > 0 && <> {decidedSummary(rows)}.</>}
      </p>
    </section>
  );
}

function Row(
  { row, open, body, record, onToggle, onDiscardDraft }: {
    row: DocketRow;
    open: boolean;
    body: string;
    record?: RuleProposal;
    onToggle: () => void;
    onDiscardDraft?: (id: string) => void;
  },
) {
  return (
    <li className={`dqrow ${row.state}${open ? ' open' : ''}`}>
      <div className="dqrowtop">
        <span className="dqn">{row.n}</span>

        <div className="dqbody">
          <h3>{row.title}</h3>
          <p className="dqmeta">
            <GmBadge gm={row.gm} />
            <span className="dqwho">{managerShort(row.gm)}</span>
            {/* Ownership is a mark on the row that has it, never a second list. */}
            {row.mine && <span className="dqmine">yours</span>}
            {row.revote && <span className="dqrevote">re-vote</span>}
          </p>
          {/*
            An item with its own text on screen does not need a paraphrase above it — and where
            nobody has written a gist there is no gist, rather than a sentence invented to fill
            the line.
          */}
          {!open && row.gist && <p className="dqgist">{row.gist}</p>}
        </div>

        <span className={`dqstate ${row.state}`}>
          {row.state === 'on-floor' && <i className="dqdot" aria-hidden="true" />}
          {row.label}
        </span>

        <button type="button" className="dqread" aria-expanded={open} onClick={onToggle}>
          {open ? 'Close' : 'Read it'}
        </button>
      </div>

      {open && (
        <div className="dqopen">
          {/*
            THE STALE DRAFT, in one line. A saved draft and a docketed proposal are two records of
            one intent; rendering both in full triples the page and asks the reader to diff two
            thousand words to discover they are nearly identical.
          */}
          {row.staleDraftAt && (
            <div className="dqstale">
              <span>
                An earlier draft of this is still saved from {row.staleDraftAt}. It is not what
                went on the docket.
              </span>
              {onDiscardDraft && (
                <button type="button" className="dqdiscard"
                  onClick={() => onDiscardDraft(row.id)}>Discard it</button>
              )}
            </div>
          )}

          {/*
            WHO TYPED IT, where the proposal is actually read. Two facts, and the second is the
            veto that stays with the author whatever a commissioner tidied.
          */}
          {record && <Transcribed p={record} />}

          {/* Verbatim. Never edited, reflowed, summarised in place or spell-corrected. */}
          <RichView html={body} className="dqtext" />

          {row.touches.length > 0 && (
            <div className="dqtouches">
              <span className="dqcaps">Rewrites</span>
              <Citations ids={[...row.touches]} />
            </div>
          )}
        </div>
      )}
    </li>
  );
}
